import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
    { href: "#komunikaty", label: "Komunikaty" },
    { href: "#wiadomosci", label: "Wiadomości" },
    { href: "/ogloszenia", label: "Ogłoszenia", external: true },
    { href: "/mapa", label: "Mapa osiedla", external: true },
    { href: "#grupa", label: "Grupa" },
    { href: "#kontakt", label: "Kontakt" },
    { href: "/zglos-usterke", label: "Zgłoś usterkę", external: true },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const isHome = pathname === "/";

    return (
        <header
            data-testid="navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled || !isHome
                    ? "bg-[#092E22]/95 backdrop-blur-md shadow-lg"
                    : "bg-[#092E22]/30 backdrop-blur-sm"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex items-center justify-between">
                <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 group">
                    <div className="flex flex-col leading-tight">
                        <span className="font-serif text-white text-lg md:text-xl tracking-wide drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)] group-hover:text-[#D4A537] transition-colors">Dąbrówka Wiślana</span>
                        <span className="text-[#D4A537] text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-semibold drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]">Aluzyjna 25 C/D/E/F</span>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {NAV_LINKS.map((l) => (
                        l.external ? (
                            <Link
                                key={l.href}
                                to={l.href}
                                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                className="px-4 py-2 rounded-full text-white/90 hover:text-[#D4A537] hover:bg-white/5 transition-all duration-300 text-sm font-medium"
                            >
                                {l.label}
                            </Link>
                        ) : (
                            <a
                                key={l.href}
                                href={isHome ? l.href : `/${l.href}`}
                                data-testid={`nav-link-${l.label.toLowerCase()}`}
                                className="px-4 py-2 rounded-full text-white/90 hover:text-[#D4A537] hover:bg-white/5 transition-all duration-300 text-sm font-medium"
                            >
                                {l.label}
                            </a>
                        )
                    ))}
                </nav>

                <button
                    aria-label="Otwórz menu"
                    data-testid="navbar-mobile-toggle"
                    onClick={() => setOpen((o) => !o)}
                    className="lg:hidden p-2 rounded-full text-white hover:bg-white/10 transition-colors"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="lg:hidden overflow-hidden bg-[#092E22] border-t border-white/10"
                    >
                        <div className="px-6 py-4 flex flex-col gap-1">
                            {NAV_LINKS.map((l, i) => (
                                l.external ? (
                                    <motion.div
                                        key={l.href}
                                        initial={{ x: -20, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        transition={{ delay: i * 0.05 }}
                                    >
                                        <Link
                                            to={l.href}
                                            onClick={() => setOpen(false)}
                                            data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                            className="block py-3 px-4 rounded-xl text-white hover:bg-white/5 hover:text-[#D4A537] transition-colors"
                                        >
                                            {l.label}
                                        </Link>
                                    </motion.div>
                                ) : (
                                    <motion.a
                                        key={l.href}
                                        href={isHome ? l.href : `/${l.href}`}
                                        onClick={() => setOpen(false)}
                                        initial={{ x: -20, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        transition={{ delay: i * 0.05 }}
                                        data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                        className="py-3 px-4 rounded-xl text-white hover:bg-white/5 hover:text-[#D4A537] transition-colors"
                                    >
                                        {l.label}
                                    </motion.a>
                                )
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Navbar;
